/**
 * Search redirects + query tweaks shared by the server (SearchController) and the
 * browser (autocomplete dropdown, search page). Pure functions, no server imports,
 * so the client bundle can import this file directly.
 *
 * Paths returned here have NO locale prefix — callers add '/fr' for French pages.
 */
import { searchResults as rolexFamilies } from '../../Constants/Rolex';

export type SearchRedirectKind = 'rolex' | 'tagHeuer' | 'notCarried';

export interface SearchRedirect {
  kind: SearchRedirectKind;
  path: string;
}

/** Lower-case, trim and collapse whitespace so "  Rolex   Datejust " === "rolex datejust" */
export const normalizeQuery = (query: unknown): string => (
  typeof query === 'string' ? query.toLowerCase().trim().replace(/\s+/g, ' ') : ''
);

/** Whole-word test — "tag" must not match "vintage", "heritage", "stage"… */
const hasWord = (q: string, words: string[]): boolean => words.some((w) => (
  new RegExp(`(^|[^a-z0-9])${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^a-z0-9])`).test(q)
));

const TAG_HEUER_WORDS = ['tag heuer', 'tagheuer', 'tag', 'heuer', 'aquaracer', 'carrera', 'formula 1', 'monaco'];
// "dog tag" pendants are jewellery, not TAG Heuer
const TAG_HEUER_EXCEPTIONS = ['dog tag', 'dog tags', 'dogtag', 'dogtags'];

// Brands we don't carry → all timepieces
const NOT_CARRIED_WORDS = ['cartier', 'iwc'];

/**
 * Where a search should go instead of the results page, or null for a normal search.
 * Rolex guidelines: every Rolex search lands in the official Rolex section, never on products.
 */
export const getSearchRedirect = (query: unknown): SearchRedirect | null => {
  const q = normalizeQuery(query);
  if (!q) return null;

  // 1. Exact Rolex family names ("datejust", "rolex datejust") → family page
  const family = rolexFamilies.find((f) => f.values.includes(q));
  if (family?.url) return { kind: 'rolex', path: family.url };

  // 2. Anything else mentioning Rolex → Discover Rolex
  if (hasWord(q, ['rolex', 'rolexes'])) return { kind: 'rolex', path: '/rolex/discover' };

  // 3. TAG Heuer (inquiry-only, not in the product index) → brand page
  if (hasWord(q, TAG_HEUER_WORDS) && !hasWord(q, TAG_HEUER_EXCEPTIONS)) {
    return { kind: 'tagHeuer', path: '/collections/timepieces/tag-heuer' };
  }

  // 4. Brands we don't carry → all timepieces
  if (hasWord(q, NOT_CARRIED_WORDS)) return { kind: 'notCarried', path: '/collections/timepieces' };

  return null;
};

/** '/fr' on French pages, '' otherwise */
export const localePrefix = (locale: string | undefined | null): string => (locale === 'fr' ? '/fr' : '');

/**
 * Generic watch searches ("watches", "mens watches", "gold watch") — titles like
 * "WOLF 1834 Watch Roll" otherwise outrank "...41mm Watch", so real watches get a boost.
 * Whole-query match only: "watch winder", "watch box", "watch strap" are left alone.
 */
const GENERIC_WATCH = new RegExp(
  '^(?:(?:mens?|men\'s|womens?|women\'s|ladies|lady|gold|silver|steel|automatic|quartz|luxury|dress|'
  + 'dive|diver|chronograph|swiss|sport|new) )?watch(?:es)?(?: for (?:men|women|him|her))?$',
);
export const isGenericWatchQuery = (query: unknown): boolean => GENERIC_WATCH.test(normalizeQuery(query));

/** Boost that puts real watches first (high score so it outweighs personalization boosts) */
export const WATCH_BOOST_FILTER = 'product_type:Timepieces<score=10>';
